import { useEffect, useRef, useState } from "react";
import type { CSSProperties, MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent } from "react";
import { osStore, type DesktopItem } from "../../os/store";
import { contextMenuStore, type ContextMenuItem } from "../../os/context-menu";
import { WidgetHost } from "../WidgetHost";
import { IconTile } from "./IconTile";
import { useDragSnap } from "./useDragSnap";
import { CELL, GAP } from "./grid-constants";
import { AppWindow, Maximize2, Pin, PinOff, Trash2 } from "lucide-react";
import { nearestFit } from "../../os/desktop-grid-geometry.mjs";
import { decideDrop, dockInsertIndex, expandRect, DOCK_HIT_PAD } from "../../os/dock-drop.mjs";
import { dropTargets } from "../../os/drop-targets";
import { desktopPicker } from "./desktop-picker";
import { SearchPicker, type PickerItem } from "../SearchPicker";

type AppManifest = (typeof osStore.packages)[number]["manifest"];

/**
 * Enabled apps this account is actually served, by module id.
 *
 * One lookup with two jobs: it gates what the grid renders, and it is the
 * `visible` input to the dock-drop decision. Read fresh rather than closed
 * over, because the drop handler runs from a window listener bound at
 * pointerdown and would otherwise see the package list as it was then.
 */
function visibleApps(): Map<string, AppManifest> {
  const m = new Map<string, AppManifest>();
  for (const p of osStore.packages) {
    if (p.state === "enabled" && p.manifest.kind === "app") m.set(p.manifest.id, p.manifest);
  }
  return m;
}

function appLabel(man: AppManifest | undefined): string | undefined {
  return man?.dock?.label || man?.name;
}

/**
 * The dock's hit box: its live rect plus a tolerance (see DOCK_HIT_PAD).
 *
 * One helper for both call sites on purpose. The live affordance and the drop
 * decision have to hit-test the SAME box or the dock lights up over an area
 * that then refuses the release — which is indistinguishable, from the user's
 * side, from the drop being broken.
 */
function dockHitRect() {
  return expandRect(dropTargets.rect("dock"), DOCK_HIT_PAD);
}

function tileGeom(col: number, row: number, cols: number, rows: number) {
  return {
    left: col * (CELL + GAP),
    top: row * (CELL + GAP),
    width: cols * CELL + (cols - 1) * GAP,
    height: rows * CELL + (rows - 1) * GAP,
  };
}

/**
 * "Add app…" / "Add widget…" open a searchable picker rather than a flat menu —
 * a context menu listing every installed app stops being usable once there are
 * more than a screenful. Same UI as the menubar finder (see SearchPicker).
 * Exported so the desktop-surface right-click (DesktopChrome) can invoke them.
 */
export function openAddAppMenu() {
  desktopPicker.open("app");
}

export function openAddWidgetMenu() {
  desktopPicker.open("widget");
}

/**
 * Right-click menu for a desktop tile.
 *
 * Icons are passed as Lucide COMPONENTS, not as name strings. ContextMenuItem's
 * type allows either, but ContextMenuHost only renders `typeof icon ===
 * "function"` — so the strings this used to pass ("AppWindow", "Trash2") drew
 * an empty icon slot with no error anywhere. dockAppContextItems in
 * DesktopChrome had it right; this now matches it.
 */
function openTileMenu(x: number, y: number, it: DesktopItem) {
  // A widget's only full view IS its app window, so there is no distinct "Open".
  const items: ContextMenuItem[] =
    it.type === "widget"
      ? [
          {
            id: "open-as-app",
            label: "Open as app",
            icon: Maximize2,
            action: () => osStore.openApp(it.moduleId),
          },
        ]
      : [{ id: "open", label: "Open", icon: AppWindow, action: () => osStore.openApp(it.moduleId) }];

  // Pinning is an APP operation. A widget is a live surface with a size and the
  // dock is a row of 26px glyphs, so the item is absent rather than disabled —
  // there is no state in which it would become available for a widget.
  //
  // This is the reliable path to a pin: drag-to-dock is a direct-manipulation
  // gesture with a small target, and it must not be the only way in.
  if (it.type === "icon") {
    const pinned = osStore.isPinned(it.moduleId);
    items.push({
      id: "pin",
      label: pinned ? "Unpin from Dock" : "Pin to Dock",
      icon: pinned ? PinOff : Pin,
      action: () => {
        const name = appLabel(visibleApps().get(it.moduleId)) || it.moduleId;
        if (pinned) {
          osStore.unpinApp(it.moduleId);
          osStore.notify(`${name} removed from the Dock`, "info");
          return;
        }
        // pinApp fails closed on visibility; only claim success if it wrote.
        if (osStore.pinApp(it.moduleId)) osStore.notify(`${name} pinned to the Dock`, "success");
        else osStore.notify(`${name} can't be pinned right now`, "warn");
      },
    });
  }

  items.push({ id: "tile-sep", separator: true });
  items.push({
    id: "remove",
    label: "Remove from desktop",
    icon: Trash2,
    danger: true,
    action: () => osStore.removeDesktopItem(it.id),
  });
  contextMenuStore.open(x, y, { kind: "app", appId: it.moduleId }, items);
}

export function DesktopGrid() {
  const [, bump] = useState(0);
  /** Horizontal inset that centres the cell field within the surface. */
  const [offsetX, setOffsetX] = useState(0);
  const hostRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => osStore.subscribe(() => bump((n) => n + 1)), []);
  useEffect(() => desktopPicker.subscribe(() => bump((n) => n + 1)), []);

  // Keep the store's logical grid width in sync with the desktop surface.
  // Measure the container, not window.innerWidth: the two differ whenever a
  // scrollbar or any layout inset applies, and a too-small gridCols silently
  // strands the right-hand columns (nothing can be placed or dropped there).
  useEffect(() => {
    const apply = () => {
      const w = hostRef.current?.clientWidth || window.innerWidth;
      const cols = Math.max(1, Math.floor(w / (CELL + GAP)));
      osStore.setGridCols(cols);
      // Centre the cell field. Tiles are laid out from x=0, and the width is
      // rarely an exact multiple of a cell, so ALL the leftover used to pile up
      // on the right — a tile in the last column sat visibly further from the
      // edge than one in the first. Splitting the remainder makes the two
      // margins equal. The last cell has no trailing gap, hence the -GAP.
      const span = cols * (CELL + GAP) - GAP;
      setOffsetX(Math.max(0, Math.round((w - span) / 2)));
    };
    apply();
    const ro =
      typeof ResizeObserver !== "undefined" && hostRef.current
        ? new ResizeObserver(apply)
        : null;
    if (ro && hostRef.current) ro.observe(hostRef.current);
    window.addEventListener("resize", apply);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", apply);
    };
  }, []);

  // A release is EITHER a grid move OR a dock pin — never both. Dropping onto
  // the dock deliberately leaves the tile's stored cell untouched, so the icon
  // springs back to where it came from; that return trip is the signal that
  // pinning copied the app rather than consuming the desktop icon (macOS does
  // the same thing, and "my icon vanished" is the failure mode it avoids).
  const { dragId, dragDelta, dragPoint, startDrag } = useDragSnap({
    onDrop: (id, col, row, point) => {
      const it = osStore.displayDesktopItems().find((d) => d.id === id);
      if (!it) return;
      const man = visibleApps().get(it.moduleId);
      const label = appLabel(man);
      const decision = decideDrop({
        point,
        dockRect: dockHitRect(),
        kind: it.type,
        pinned: osStore.isPinned(it.moduleId),
        // Widgets are refused before this is read; passing the honest value
        // anyway keeps the call site free of a special case that could rot.
        visible: it.type === "widget" ? true : !!man,
        label,
      });
      dropTargets.setHover(null);

      if (decision.action === "grid-move") {
        osStore.moveDesktopItem(id, col, row);
        return;
      }
      if (decision.action === "refuse") {
        // Silence here would read as a broken drop: the icon flies back and
        // nothing happens. Say which of the three reasons it was.
        if (decision.message) osStore.notify(decision.message, "warn");
        return;
      }
      // Where the caret was showing when the user let go. Measured BEFORE the
      // pin exists, which is exactly the list reorderPin expects a gap index
      // against — pinApp appends, so the new item's own slot is the end and
      // every gap the caret could show is to its left and needs no adjusting.
      const gap = dockInsertIndex(point, dropTargets.itemRects("dock"));

      // pinApp fails closed independently of this decision; only claim success
      // if it actually wrote a pin.
      if (osStore.pinApp(it.moduleId)) {
        // The caret promised a position; honour it rather than always
        // appending, or the affordance is a lie the moment the dock has pins.
        osStore.reorderPin(it.moduleId, gap);
        osStore.notify(`${label || it.moduleId} pinned to the Dock`, "success");
      }
    },
  });

  // Manifest lookup for icon tiles (cheap; recomputed each render after a store bump).
  //
  // This map is also the render gate. It is built from osStore.packages, which
  // is the server-filtered list for THIS account — so an icon whose manifest is
  // missing is an app the account may not see, and gets skipped below. It used
  // to fall back to showing the raw module id, which meant a desktop left behind
  // by another account still advertised "app.analytics" as a tile.
  const appById = visibleApps();

  // While dragging, show the grid and preview the landing slot. The candidate
  // cell is derived with the SAME maths useDragSnap uses on drop, then resolved
  // through nearestFit — so the highlight can never promise a cell the drop
  // would reject (occupied / off-grid targets show where it will actually land).
  const gridCols = osStore.gridCols();
  // Render from the DISPLAY layout (stored intent fitted to the live width), so
  // narrowing never rewrites what the user arranged.
  const view = osStore.displayDesktopItems();
  const dragItem = dragId ? view.find((d) => d.id === dragId) : undefined;

  // Mirror of the drop decision, evaluated live so the affordance and the
  // outcome can never disagree. Same function, same inputs — the only
  // difference is the pointer position is in-flight rather than final.
  const dragMan = dragItem ? appById.get(dragItem.moduleId) : undefined;
  const dockDecision = dragItem
    ? decideDrop({
        point: dragPoint,
        dockRect: dockHitRect(),
        kind: dragItem.type,
        pinned: osStore.isPinned(dragItem.moduleId),
        visible: dragItem.type === "widget" ? true : !!dragMan,
        label: appLabel(dragMan),
      })
    : null;
  const overDock = dockDecision?.overDock ?? false;
  const dockAccepts = dockDecision?.action === "pin";
  const dockIndex = overDock ? dockInsertIndex(dragPoint, dropTargets.itemRects("dock")) : 0;

  // Publish hover so the dock can light up. Via the registry, not a ref into
  // the dock's DOM — the grid never learns what a dock is.
  useEffect(() => {
    dropTargets.setHover(overDock ? { targetId: "dock", index: dockIndex, accepted: dockAccepts } : null);
  }, [overDock, dockIndex, dockAccepts]);
  // Unmounting mid-drag must not leave the dock stuck highlighted.
  useEffect(() => () => dropTargets.setHover(null), []);

  let dropSlot: ReturnType<typeof tileGeom> | null = null;
  // While the dock owns the pointer there is no landing slot to promise.
  // Drawing both would tell the user the drop does two things.
  if (dragItem && !overDock) {
    const from = tileGeom(dragItem.col, dragItem.row, dragItem.cols, dragItem.rows);
    const col = Math.max(0, Math.round((from.left + dragDelta.dx) / (CELL + GAP)));
    const row = Math.max(0, Math.round((from.top + dragDelta.dy) / (CELL + GAP)));
    const dest = nearestFit(
      view,
      dragItem.id,
      col,
      row,
      dragItem.cols,
      dragItem.rows,
      gridCols
    );
    dropSlot = tileGeom(dest.col, dest.row, dragItem.cols, dragItem.rows);
  }

  // Guides are drawn only around the LIVE landing slot — the tile's footprint
  // expanded by one cell each side — rather than over the whole surface. Keyed
  // off dropSlot (the resolved destination), not the tile's stored origin,
  // which would leave the halo behind as the tile moves.
  const halo = dropSlot
    ? {
        left: dropSlot.left - (CELL + GAP),
        top: dropSlot.top - (CELL + GAP),
        width: dropSlot.width + 2 * (CELL + GAP),
        height: dropSlot.height + 2 * (CELL + GAP),
      }
    : null;

  // Candidates for the "add to desktop" picker, in the shared PickerItem shape.
  const pickerKind = desktopPicker.kind;
  const pickerItems: PickerItem[] =
    pickerKind === "widget"
      ? osStore.widgetModules().map((m) => ({
          id: m.id,
          name: m.name,
          icon: m.dock?.icon,
          subtitle: `${m.id} · ${m.widget?.defaultCols ?? 1}×${m.widget?.defaultRows ?? 1}`,
        }))
      : osStore.packages
          .filter((p) => p.state === "enabled" && p.manifest.kind === "app")
          .map((p) => ({
            id: p.manifest.id,
            name: p.manifest.dock?.label || p.manifest.name,
            icon: p.manifest.dock?.icon,
            subtitle: p.manifest.id + (p.defaultVersion ? " · v" + p.defaultVersion : ""),
          }));

  const addPicked = (item: PickerItem) => {
    if (pickerKind === "widget") {
      const m = osStore.widgetModules().find((w) => w.id === item.id);
      osStore.addDesktopItem("widget", item.id, m?.widget?.defaultCols ?? 1, m?.widget?.defaultRows ?? 1);
    } else {
      osStore.addDesktopItem("icon", item.id);
    }
  };

  return (
    // `is-dragging` on the GRID, not just the tile. .os-desktop-grid is
    // position+z-index, so it opens a stacking context: the tile's own
    // z-index:5 can only sort it against its siblings INSIDE the grid, and can
    // never lift it past .dock-wrap (z-index:40), which is a sibling of the
    // grid. So a tile dragged toward the dock slid underneath it and vanished —
    // the whole interaction happened where the user could not see it. Raising
    // the context itself for the duration of the drag is what puts the tile on
    // top; see the rule in os.css for the ceiling it stops below.
    <div className={"os-desktop-grid" + (dragId ? " is-dragging" : "")} ref={hostRef}>
      <SearchPicker
        open={pickerKind !== null}
        items={pickerItems}
        placeholder={pickerKind === "widget" ? "Search widgets…" : "Search apps…"}
        emptyText={pickerKind === "widget" ? "No widgets installed" : "No apps match"}
        ariaLabel={pickerKind === "widget" ? "Add a widget" : "Add an app"}
        footer="Up/Down navigate · Enter add to desktop · Esc close"
        onPick={addPicked}
        onClose={() => desktopPicker.close()}
      />
      {/* Everything cell-positioned lives in here. The offset is on the WRAPPER,
          not on each tile, so tile coordinates stay wrapper-relative and the
          drag maths (a wrapper-relative origin plus a pure pointer delta) needs
          no adjustment at all. */}
      <div className="os-desktop-field" style={{ transform: `translateX(${offsetX}px)` }}>
      {halo && (
        <div
          className="os-grid-guides"
          style={
            {
              ...halo,
              "--os-grid-step": `${CELL + GAP}px`,
              /* The halo moves, but the lattice must not: offsetting the
                 background origin by the halo's own position keeps the lines on
                 the GLOBAL cell grid instead of restarting at the halo's edge,
                 which would make them drift under the dragged tile. */
              backgroundPosition: `${-halo.left}px ${-halo.top}px`,
            } as CSSProperties
          }
        />
      )}
      {dropSlot && <div className="os-grid-drop" style={dropSlot} />}
      {view.map((it) => {
        const g = tileGeom(it.col, it.row, it.cols, it.rows);
        const dragging = dragId === it.id;
        const style: CSSProperties = {
          left: g.left,
          top: g.top,
          width: g.width,
          height: g.height,
          transform: dragging
            ? `translate(${dragDelta.dx}px, ${dragDelta.dy}px) scale(1.04)`
            : undefined,
          // raised z-index + lift shadow while dragging come from .is-dragging
        };
        const man = appById.get(it.moduleId);
        // No manifest, no tile. displayDesktopItems() already dropped these once
        // packages loaded; this also covers the pre-fetch window, where the old
        // code drew a tile labelled with the bare module id — which is how a
        // desktop left by another account still advertised "app.analytics".
        if (it.type === "icon" && !man) return null;
        return (
          <div
            key={it.id}
            className={"os-desktop-tile" + (dragging ? " is-dragging" : "")}
            style={style}
            onPointerDown={(e: ReactPointerEvent) => {
              if (e.button !== 0) return; // left button only
              startDrag(it.id, e, g.left, g.top);
            }}
            onContextMenu={(e: ReactMouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              openTileMenu(e.clientX, e.clientY, it);
            }}
          >
            {it.type === "icon" ? (
              <IconTile
                moduleId={it.moduleId}
                label={man?.dock?.label || man?.name || ""}
                icon={man?.dock?.icon}
              />
            ) : (
              <WidgetHost
                moduleId={it.moduleId}
                placementId={it.id}
                cols={it.cols}
                rows={it.rows}
                surface="desktop"
              />
            )}
          </div>
        );
      })}
      </div>
    </div>
  );
}
